import {
	Actor,
	type ActorSubclass,
	HttpAgent,
	type Identity,
} from "@icp-sdk/core/agent";
import type { IcClientConfig } from "./config";
import { idlFactory } from "./declarations/protocol.did.js";
import type { _SERVICE } from "./declarations/protocol.did.d.ts";

export type ProtocolActor = ActorSubclass<_SERVICE>;

/**
 * Create HttpAgent. For local replica, never use a canister subdomain as host
 * (that would route IC API through the assets canister). Callers should pass
 * VITE_IC_HOST=http://127.0.0.1:8000.
 */
export async function createHttpAgent(
	config: Pick<IcClientConfig, "host" | "fetchRootKey">,
	identity?: Identity,
): Promise<HttpAgent> {
	const host = normalizeAgentHost(config.host);
	const agent = await HttpAgent.create({
		host,
		identity,
	});
	if (config.fetchRootKey) {
		try {
			await agent.fetchRootKey();
		} catch (first) {
			// One retry — local replica occasionally races after restart
			await new Promise((r) => setTimeout(r, 200));
			try {
				await agent.fetchRootKey();
			} catch (second) {
				const msg =
					second instanceof Error
						? second.message
						: first instanceof Error
							? first.message
							: "fetchRootKey failed";
				throw new Error(
					`Local root key failed (host=${host}): ${msg}. Use SPA at http://127.0.0.1:8000/?canisterId=<assets> and ensure icp network is up.`,
				);
			}
		}
	}
	return agent;
}

/** Strip canister-subdomain hosts so API hits the replica root. */
function normalizeAgentHost(host: string): string {
	const raw = (host || "").trim() || "http://127.0.0.1:8000";
	try {
		const u = new URL(raw);
		const h = u.hostname.toLowerCase();
		// {canisterId}.localhost → 127.0.0.1 (same port)
		if (h.endsWith(".localhost") && h !== "localhost") {
			u.hostname = "127.0.0.1";
			return u.origin;
		}
		return u.origin;
	} catch {
		return raw;
	}
}

export function createProtocolActor(
	protocolCanisterId: string,
	agent: HttpAgent,
): ProtocolActor {
	if (!protocolCanisterId?.trim()) {
		throw new Error("protocolCanisterId is empty — bake VITE_CANISTER_ID_PROTOCOL");
	}
	return Actor.createActor<_SERVICE>(idlFactory, {
		canisterId: protocolCanisterId,
		agent,
	});
}

export async function createProtocolActorWithIdentity(
	config: IcClientConfig,
	identity: Identity,
): Promise<{ agent: HttpAgent; actor: ProtocolActor }> {
	const agent = await createHttpAgent(config, identity);
	const actor = createProtocolActor(config.protocolCanisterId, agent);
	return { agent, actor };
}
